import * as React from 'react';
import type { ReactNode } from 'react';

import { isBlankValue } from '../lib/is-blank-value.js';
import { cn } from '../lib/utils.js';

export function DetailGrid({
  children,
  columns = 1,
  justified = false,
  align = 'baseline',
  sectioned = false,
  className,
}: {
  children: ReactNode;
  // 2 pairs each label with its value on one row — denser for a card with many short fields.
  columns?: 1 | 2;
  // Pushes each value to the right edge and quiets its label — for a narrow rail where a ragged left-aligned value column reads as an accident.
  justified?: boolean;
  align?: 'baseline' | 'start';
  sectioned?: boolean;
  className?: string;
}) {
  const List = sectioned ? 'div' : 'dl';
  const alignClass = align === 'start' ? 'items-start' : 'items-baseline';

  if (justified) {
    return (
      <List
        className={cn(
          'grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm',
          alignClass,
          '[&>dt]:text-muted-foreground [&>dt]:font-normal',
          '[&>dd]:min-w-0 [&>dd]:text-right [&>dd]:font-medium [&>dd]:break-words',
          className,
        )}
      >
        {children}
      </List>
    );
  }

  return (
    <List
      className={cn(
        columns === 2
          ? cn('grid max-w-lg grid-cols-[max-content_1fr] gap-x-4 gap-y-2 text-sm', alignClass)
          : 'grid max-w-lg gap-3 text-sm',
        className,
      )}
    >
      {children}
    </List>
  );
}

export function DetailRow({
  label,
  value,
  hideIfEmpty = true,
  icon,
  placeholder,
}: {
  label: string;
  value: ReactNode;
  hideIfEmpty?: boolean;
  icon?: React.ReactElement<{ className?: string; 'aria-hidden'?: boolean }>;
  placeholder?: ReactNode;
}) {
  const blank = isBlankValue(value);
  if (blank && hideIfEmpty && placeholder === undefined) {
    return null;
  }

  return (
    <>
      <dt className={cn('font-medium', icon && 'flex items-center gap-1.5')}>
        {icon
          ? React.cloneElement(icon, {
              'aria-hidden': true,
              className: cn('size-4 shrink-0', icon.props.className),
            })
          : null}
        {icon ? <span>{label}</span> : label}
      </dt>
      <dd>
        {blank && placeholder !== undefined ? (
          <span className="font-normal text-muted-foreground">{placeholder}</span>
        ) : (
          value
        )}
      </dd>
    </>
  );
}
